import { useState } from "react";
import { fetchSosMessageHistory } from "../utils/clientApi";
import type { SosMessageLog, RecipientType } from "../types/Client";

const PAGE_SIZE = 3;

export function useSosMessageLog() {
  const [openMsgLogFor, setOpenMsgLogFor] = useState<number | null>(null);

  const [msgLogCache, setMsgLogCache] = useState<Record<number, SosMessageLog[]>>({});
  const [msgLogLoading, setMsgLogLoading] = useState<Record<number, boolean>>({});
  const [msgLogError, setMsgLogError] = useState<Record<number, string | null>>({});
  const [msgLogFilter, setMsgLogFilter] = useState<Record<number, RecipientType | null>>({});

  const [msgLogPage, setMsgLogPage] = useState<Record<number, number>>({});            // 현재 페이지(0-based)
  const [msgLogTotalPages, setMsgLogTotalPages] = useState<Record<number, number>>({}); // 총 페이지 수

  async function ensureMsgLogLoaded(clientId: number, recipientType: RecipientType | null = null) {
    try {
      setMsgLogLoading(prev => ({ ...prev, [clientId]: true }));
      setMsgLogError(prev => ({ ...prev, [clientId]: null }));

      const page = await fetchSosMessageHistory({
        clientId,
        page: 0,
        size: PAGE_SIZE,
        sort: "id,DESC",
        recipientType,
      });

      setMsgLogCache(prev => ({ ...prev, [clientId]: page.content ?? [] }));
      setMsgLogPage(prev => ({ ...prev, [clientId]: 0 }));
      setMsgLogTotalPages(prev => ({ ...prev, [clientId]: page.totalPages ?? 0 }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "조회 실패";
      setMsgLogError(prev => ({ ...prev, [clientId]: msg }));
    } finally {
      setMsgLogLoading(prev => ({ ...prev, [clientId]: false }));
    }
  }

  async function loadMsgLogPage(clientId: number, targetPage: number) {
    const recipientType = msgLogFilter[clientId] ?? null;
    try {
      setMsgLogLoading(prev => ({ ...prev, [clientId]: true }));
      const page = await fetchSosMessageHistory({
        clientId,
        page: targetPage,
        size: PAGE_SIZE,
        sort: "id,DESC",
        recipientType,
      });
      setMsgLogCache(prev => ({ ...prev, [clientId]: page.content ?? [] }));
      setMsgLogPage(prev => ({ ...prev, [clientId]: targetPage }));
      setMsgLogTotalPages(prev => ({ ...prev, [clientId]: page.totalPages ?? 0 }));
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "페이지 이동 실패";
      setMsgLogError(prev => ({ ...prev, [clientId]: msg }));
    } finally {
      setMsgLogLoading(prev => ({ ...prev, [clientId]: false }));
    }
  }

  function nextMsgLogPage(clientId: number) {
    const cur = msgLogPage[clientId] ?? 0;
    const total = msgLogTotalPages[clientId] ?? 0;
    if (cur < total - 1) loadMsgLogPage(clientId, cur + 1);
  }

  function prevMsgLogPage(clientId: number) {
    const cur = msgLogPage[clientId] ?? 0;
    if (cur > 0) loadMsgLogPage(clientId, cur - 1);
  }

  async function toggleMsgLogCard(clientId: number) {
    if (openMsgLogFor === clientId) {
      setOpenMsgLogFor(null);
      return;
    }
    setOpenMsgLogFor(clientId);
    const currentFilter = msgLogFilter[clientId] ?? null; // null=전체
    await ensureMsgLogLoaded(clientId, currentFilter);
  }

  async function applyMsgLogFilter(clientId: number, value: string) {
    const v = (value === "" ? null : (value as RecipientType));
    setMsgLogFilter(prev => ({ ...prev, [clientId]: v }));
    await ensureMsgLogLoaded(clientId, v);
  }

  return {
    openMsgLogFor,
    msgLogCache,
    msgLogLoading,
    msgLogError,
    msgLogFilter,

    toggleMsgLogCard,
    applyMsgLogFilter,
    setOpenMsgLogFor,

    msgLogPage,
    msgLogTotalPages,
    prevMsgLogPage,
    nextMsgLogPage,
  };
}